/**
 * GenericModulePage — shared scaffold for C4-C9 (Imaging / Insurance / Blood Bank /
 * Store / Mortuary / Canteen). Each is a hospital-scoped CRUD list with custom fields.
 *
 * Pages declare their `config` (title, icon, permPrefix, fields[]). Each field describes
 *  - key: storage key
 *  - label: visible label
 *  - type: text | number | date | textarea | select
 *  - options: array of strings (for select)
 *  - required: bool
 *  - listColumn: bool — whether to show in the list table
 */
import { useState, useEffect, useCallback, useMemo } from 'react';
import { useAuth } from '../lib/auth';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Plus, Search, Trash2, Pencil, Printer } from 'lucide-react';
import { toast } from 'sonner';
import { format } from 'date-fns';

export default function GenericModulePage({ config, apiClient }) {
  const { user, hasPermission } = useAuth();
  const { title, subtitle, icon: Icon, permPrefix, fields, testId, idLabel, printPathBuilder } = config;
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);

  const canView = hasPermission(`${permPrefix}.view`);
  const canManage = hasPermission(`${permPrefix}.manage`);

  const fetchAll = useCallback(async () => {
    setLoading(true);
    try { const r = await apiClient.list(); setItems(r.data || []); }
    catch (err) { toast.error(err.response?.data?.detail || `Failed to load ${title}`); }
    finally { setLoading(false); }
  }, [apiClient, title]);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  const listColumns = useMemo(() => fields.filter(f => f.listColumn !== false), [fields]);
  const filtered = useMemo(() => {
    const s = search.trim().toLowerCase();
    if (!s) return items;
    return items.filter(it => fields.some(f => String(it[f.key] || '').toLowerCase().includes(s)));
  }, [items, search, fields]);

  const remove = async (it) => {
    if (!window.confirm(`Delete this ${idLabel || 'item'}?`)) return;
    try { await apiClient.delete(it.id); toast.success('Deleted'); fetchAll(); }
    catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
  };

  if (!user?.is_super_admin && !canView) {
    return (
      <div className="min-h-[60vh] flex items-center justify-center" data-testid={`${testId}-restricted`}>
        <Card className="max-w-lg w-full"><CardHeader><CardTitle>Access Restricted</CardTitle></CardHeader>
          <CardContent>You don't have permission to view {title}.</CardContent></Card>
      </div>
    );
  }

  return (
    <div className="space-y-4" data-testid={`${testId}-page`}>
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3">
        <div className="flex items-center gap-2">
          {Icon && <Icon className="text-emerald-600" />}
          <div>
            <h1 className="text-2xl font-semibold text-slate-900">{title}</h1>
            {subtitle && <p className="text-sm text-slate-500">{subtitle}</p>}
          </div>
        </div>
        {canManage && (
          <Button onClick={() => { setEditing(null); setOpen(true); }} className="bg-emerald-600 hover:bg-emerald-700" data-testid={`${testId}-new-btn`}>
            <Plus size={16} className="mr-1" /> New {idLabel || 'Record'}
          </Button>
        )}
      </div>
      <Card>
        <CardContent className="pt-4 space-y-3">
          <div className="relative">
            <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
            <Input data-testid={`${testId}-search`} className="pl-9" placeholder="Search…" value={search} onChange={(e) => setSearch(e.target.value)} />
          </div>
          {loading ? (
            <div className="py-10 text-center text-sm text-slate-500">Loading…</div>
          ) : filtered.length === 0 ? (
            <div className="py-10 text-center text-sm text-slate-500" data-testid={`${testId}-empty`}>No records yet.</div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="text-xs uppercase tracking-wider text-slate-500 border-b">
                  <tr>
                    {listColumns.map(c => <th key={c.key} className="text-left py-2 px-2">{c.label}</th>)}
                    <th className="text-left py-2 px-2">Created</th>
                    {(canManage || printPathBuilder) && <th />}
                  </tr>
                </thead>
                <tbody>
                  {filtered.map(it => (
                    <tr key={it.id} className="border-b hover:bg-slate-50" data-testid={`${testId}-row-${it.id}`}>
                      {listColumns.map(c => (
                        <td key={c.key} className="py-2 px-2">{formatCellValue(it[c.key], c.type)}</td>
                      ))}
                      <td className="py-2 px-2 text-xs text-slate-500">{it.created_at ? format(new Date(it.created_at), 'dd MMM yy') : ''}</td>
                      {(canManage || printPathBuilder) && (
                        <td className="py-2 px-2 text-right whitespace-nowrap">
                          {printPathBuilder && (
                            <Button
                              size="icon"
                              variant="ghost"
                              onClick={() => window.open(printPathBuilder(it), '_blank', 'noopener')}
                              title="Print"
                              data-testid={`${testId}-print-${it.id}`}>
                              <Printer size={14} className="text-emerald-600" />
                            </Button>
                          )}
                          {canManage && <>
                            <Button size="icon" variant="ghost" onClick={() => { setEditing(it); setOpen(true); }} data-testid={`${testId}-edit-${it.id}`}><Pencil size={14} /></Button>
                            <Button size="icon" variant="ghost" onClick={() => remove(it)} data-testid={`${testId}-del-${it.id}`}><Trash2 size={14} className="text-rose-500" /></Button>
                          </>}
                        </td>
                      )}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>
      <RecordDialog open={open} onOpenChange={setOpen} editing={editing} fields={fields} apiClient={apiClient} onSaved={fetchAll} title={idLabel || 'Record'} testId={testId} />
    </div>
  );
}

const formatCellValue = (v, type) => {
  if (v === null || v === undefined || v === '') return '—';
  if (type === 'date' && typeof v === 'string') {
    try { return format(new Date(v), 'dd MMM yy'); } catch { return v; }
  }
  if (typeof v === 'object') return JSON.stringify(v);
  return String(v);
};

const RecordDialog = ({ open, onOpenChange, editing, fields, apiClient, onSaved, title, testId }) => {
  const [form, setForm] = useState({});
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open) { setForm(editing ? { ...editing } : {}); }
  }, [open, editing]);

  const update = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const submit = async () => {
    for (const f of fields) {
      if (f.required && !form[f.key]) { toast.error(`${f.label} is required`); return; }
    }
    setSaving(true);
    try {
      if (editing) { await apiClient.update(editing.id, form); toast.success(`${title} updated`); }
      else { await apiClient.create(form); toast.success(`${title} created`); }
      onSaved?.(); onOpenChange(false);
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
    finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid={`${testId}-dialog`}>
        <DialogHeader><DialogTitle>{editing ? `Edit ${title}` : `New ${title}`}</DialogTitle></DialogHeader>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          {fields.map(f => (
            <div key={f.key} className={f.fullWidth ? 'md:col-span-2' : ''}>
              <Label>{f.label}{f.required && ' *'}</Label>
              {f.type === 'textarea' ? (
                <Textarea rows={3} value={form[f.key] || ''} onChange={(e) => update(f.key, e.target.value)} placeholder={f.placeholder} data-testid={`${testId}-field-${f.key}`} />
              ) : f.type === 'select' ? (
                <Select value={form[f.key] || ''} onValueChange={(v) => update(f.key, v)}>
                  <SelectTrigger data-testid={`${testId}-field-${f.key}`}><SelectValue placeholder={f.placeholder || 'Select'} /></SelectTrigger>
                  <SelectContent>{(f.options || []).map(o => <SelectItem key={o} value={o}>{o}</SelectItem>)}</SelectContent>
                </Select>
              ) : (
                <Input
                  type={f.type === 'number' ? 'number' : f.type === 'date' ? 'date' : 'text'}
                  value={form[f.key] || ''}
                  onChange={(e) => update(f.key, f.type === 'number' ? (e.target.value === '' ? '' : parseFloat(e.target.value)) : e.target.value)}
                  placeholder={f.placeholder}
                  data-testid={`${testId}-field-${f.key}`}
                />
              )}
            </div>
          ))}
        </div>
        <DialogFooter><Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button><Button onClick={submit} disabled={saving} className="bg-emerald-600 hover:bg-emerald-700" data-testid={`${testId}-save-btn`}>{saving ? 'Saving…' : 'Save'}</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
